import moment from 'moment-timezone';
import typeOf from '../helpers/typeOf';
import config from '../config';
import UserStore from '../stores/UserStore';
import { getLocation, replace as navReplace, push as navPush } from '../utils/navigation';

const paramKeys = {
    createStartDate: 'created_at_from',
    createEndDate: 'created_at_to',
    updatedStartDate: 'updated_at_from',
    updatedEndDate: 'updated_at_to',
    trackCreatedStartDate: 'track_created_at_from',
    trackCreatedEndDate: 'track_created_at_to',
    conversionCreatedStartDate: 'conversion_created_at_from',
    conversionCreatedEndDate: 'conversion_created_at_to',
    conversionUpdatedStartDate: 'conversion_updated_at_from',
    conversionUpdatedEndDate: 'conversion_updated_at_to',
    actionTypes: 'action_types',
    actionNames: 'action_names',
    utmMedium: 'utm_medium',
    utmSource: 'utm_source',
    utmCampaign: 'utm_campaign',
    utmTerm: 'utm_term',
    utmContent: 'utm_content',
    advCids: 'adv_cids',
    publisher: 'publisher_ids',
    offer: 'offer_ids',
    goals: 'goal_ids',
    goalActions: 'goal_action_ids',
    selfieLink: 'selfie_link',
    mimeTypes: 'mime_types',
    postbackIds: 'postback_ids',
    conversionStatuses: 'conversion_statuses',
    currentConversionStatuses: 'current_conversion_statuses',
    conversions: 'conversion_ids',
    accessGranted: 'access_granted',
    offerLocales: 'offer_locales',
};

const arrayKeys = [
    'statusesSelectValue','localesSelectValue','accessSelectValue','actionTypes','actionNames','statuses',
    'goals','goalActions','conversionStatuses','currentConversionStatuses','postbackIds','offer',
    'conversions','accessGranted','offerLocales'
];

const jsonKeys = [
    'utmMedium','utmSource','utmCampaign','utmTerm','utmContent','param1','param2','param3','param4','advCids'
];

const exclude = ['metrics'];
const commonArrayKeys = arrayKeys.concat(jsonKeys);

const parseSegmentsSafe = (value) => {
    if (!value) return null;
    if (Array.isArray(value) || typeOf.isObject(value)) return value;
    if (!typeOf.isString(value)) return null;

    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

function parseSearchLike(input) {
    if (!input) return {};
    const s = typeof input === 'string'
        ? input
        : (typeof input.search === 'string' ? input.search : '');
    const q = s.startsWith('?') ? s.slice(1) : s;
    const params = new URLSearchParams(q);
    const obj = {};
    for (const [k, v] of params) {
        if (obj[k] === undefined) obj[k] = v;
        else if (Array.isArray(obj[k])) obj[k].push(v);
        else obj[k] = [obj[k], v];
    }
    return obj;
}

export function setQuery(path) {
    navReplace(path);
}

export function goTo(path) {
    navPush(path);
}

const query = {
    parse(q) {
        const timezone = UserStore.getTimeZone();
        let obj;

        if (!q) obj = {};
        else if (typeof q === 'string') obj = parseSearchLike(q);
        else if (typeof q === 'object' && typeof q.search === 'string') obj = parseSearchLike(q);
        else if (typeof q === 'object') obj = { ...q };
        else obj = {};

        for (let key in obj) {
            if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
            if (exclude.includes(key)) continue;

            let value = obj[key];

            if (commonArrayKeys.indexOf(key) !== -1) {
                if (typeOf.isString(value)) obj[key] = [{ value, title: value }];
                else if (typeOf.isArray(value)) obj[key] = value.map(item => ({ value: item, title: item }));
                else if (value != null) obj[key] = [{ value, title: String(value) }];
            } else if (typeOf.isNumeric(value)) {
                obj[key] = (value && +value) || null;
            } else if (typeOf.isString(value) && value.match(/^\d{4}-\d{2}-\d{2}$/i)) {
                obj[key] = moment.utc(value, config.codeFormat).tz(timezone);
            } else if (!value) {
                delete obj[key];
            }
        }

        return {
            ...obj,
            segments: parseSegmentsSafe(obj.segments),
        };
    },

    replace(q) {
        const norm = this.stringify({ ...q });
        const params = new URLSearchParams();

        for (let key in norm) {
            if (!Object.prototype.hasOwnProperty.call(norm, key)) continue;
            const val = norm[key];
            if (val == null) continue;

            if (Array.isArray(val)) {
                for (const v of val) {
                    if (v != null && v !== '') params.append(key, String(v));
                }
            } else {
                params.set(key, String(val));
            }
        }

        const search = params.toString();

        const wl = (typeof window !== 'undefined' && window.location) ? window.location : null;
        const pathname = (wl && wl.pathname) || (getLocation() && getLocation().pathname) || '/';
        navReplace({ pathname, search: search ? `?${search}` : '' });
    },

    stringify(data) {
        for (let key in data) {
            if (!Object.prototype.hasOwnProperty.call(data, key)) continue;

            const cur = data[key];
            let value;

            if (moment.isMoment(cur)) {
                value = cur.format(config.codeFormat);
            } else if (typeOf.isArray(cur)) {
                value = typeOf.isObject(cur[0]) ? cur.map(item => item.value) : cur;
            } else if (typeOf.isObject(cur)) {
                value = cur.value;
            } else {
                value = cur;
            }

            data[key] = value == null || value === '' ? undefined : value;
        }
        return data;
    },

    parseToSend(params = {}) {
        const timezone = UserStore.getTimeZone();
        const data = { count: config.itemsCountPerPage };

        for (let key in params) {
            if (!Object.prototype.hasOwnProperty.call(params, key)) continue;
            if (!params[key]) continue;

            let value = params[key];
            const newKey = paramKeys[key] || key;

            if (moment.isMoment(value)) {
                if (key.endsWith('StartDate')) {
                    value = value.clone().startOf('day').tz(timezone);
                } else if (key.endsWith('EndDate')) {
                    value = value.clone().endOf('day').tz(timezone);
                }
                data[newKey] = value.unix();
            } else if (jsonKeys.indexOf(key) !== -1 && value.length) {
                data[newKey] = JSON.stringify(value.map(item => item.value));
            } else if (typeOf.isArray(value)) {
                const arr = typeOf.isObject(value[0]) ? value.map(item => item.value) : value;
                if (newKey === 'sources') {
                    const hasAll = arr.includes('all');
                    if (hasAll && arr.length === 1) {
                        data[newKey] = 'all';
                    } else if (hasAll) {
                        data[newKey] = 'all';
                    } else if (arr.length) {
                        data[newKey] = arr.join(',');
                    }
                } else if (newKey === 'statuses') {
                    data[newKey] = arr.join(',');
                } else {
                    data[newKey] = arr.join(',');
                }
            } else {
                data[newKey] = value;
            }
        }

        return data;
    },
};

export default query;
